import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    $crisp?: Array<any>;
    CRISP_WEBSITE_ID?: string;
  }
}

const DEFAULT_CRISP_WEBSITE_ID = '13ec1a70-3438-4b13-837a-32b7a7f698d9';
const CRISP_WEBSITE_ID = String(import.meta.env.VITE_CRISP_WEBSITE_ID || DEFAULT_CRISP_WEBSITE_ID).trim();
export const CRISP_OPEN_EVENT = 'launchiq:open-support-chat';
export const CRISP_UNAVAILABLE_EVENT = 'launchiq:support-chat-unavailable';

const CRISP_SCRIPT_SELECTOR = 'script[data-crisp-chat="true"]';
const CRISP_OPEN_RETRIES = 12;
const CRISP_OPEN_RETRY_DELAY_MS = 75;
const CRISP_SCRIPT_URL = 'https://client.crisp.chat/l.js';

export const isCrispConfigured = () => Boolean(CRISP_WEBSITE_ID);

const runCrisp = (command: any[]) => {
  window.$crisp = window.$crisp || [];
  window.$crisp.push(command);
};

const ensureCrispGlobals = () => {
  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
};

const ensureCrispScript = () => {
  const existingScript = document.querySelector<HTMLScriptElement>(CRISP_SCRIPT_SELECTOR);
  if (existingScript) return existingScript;

  const script = document.createElement('script');
  script.src = CRISP_SCRIPT_URL;
  script.setAttribute('data-crisp-chat', 'true');
  script.defer = true;
  script.async = false;
  document.head.appendChild(script);
  return script;
};

const preloadCrispScript = () => {
  if (document.querySelector('link[rel="preload"][href*="client.crisp.chat"]')) return;

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = CRISP_SCRIPT_URL;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

const preconnectToCrisp = () => {
  if (document.querySelector('link[rel="preconnect"][href*="crisp"]')) return;

  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = 'https://client.crisp.chat';
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

const requestOpenWithRetry = (attempt = 0) => {
  runCrisp(['do', 'chat:show']);
  runCrisp(['do', 'chat:open']);
  if (attempt >= CRISP_OPEN_RETRIES) return;
  window.setTimeout(() => requestOpenWithRetry(attempt + 1), CRISP_OPEN_RETRY_DELAY_MS);
};

const openCrispChatNow = () => {
  if (!CRISP_WEBSITE_ID) {
    console.warn('[CrispChat] Cannot open support chat because VITE_CRISP_WEBSITE_ID is not configured.');
    window.dispatchEvent(new Event(CRISP_UNAVAILABLE_EVENT));
    return false;
  }

  ensureCrispGlobals();
  preloadCrispScript();
  const script = ensureCrispScript();
  requestOpenWithRetry();

  script.addEventListener('load', () => requestOpenWithRetry(), { once: true });
  script.addEventListener('error', () => {
    window.dispatchEvent(new Event(CRISP_UNAVAILABLE_EVENT));
  }, { once: true });

  return true;
};

export const openCrispSupport = () => openCrispChatNow();

export default function CrispChat({
  enabled,
  shopDomain,
  planName,
}: {
  enabled: boolean;
  shopDomain?: string | null;
  planName?: string | null;
}) {
  const location = useLocation();

  useEffect(() => {
    if (!CRISP_WEBSITE_ID) return;
    preconnectToCrisp();
  }, []);

  useEffect(() => {
    if (!CRISP_WEBSITE_ID) return;

    ensureCrispGlobals();
    if (!enabled) return;

    const script = ensureCrispScript();
    const handleScriptError = () => {
      window.dispatchEvent(new Event(CRISP_UNAVAILABLE_EVENT));
    };
    const handleScriptLoad = () => {
      runCrisp(['do', 'chat:show']);
    };

    if (!script.hasAttribute('data-listeners-attached')) {
      script.addEventListener('error', handleScriptError);
      script.addEventListener('load', handleScriptLoad);
      script.setAttribute('data-listeners-attached', 'true');
    }
  }, [enabled]);

  useEffect(() => {
    if (!CRISP_WEBSITE_ID) return;

    runCrisp(['set', 'session:data', [[
      ['App', 'LaunchIQ'],
      ['Shop', String(shopDomain || 'Unavailable')],
      ['Plan', String(planName || 'unknown')],
      ['Page', location.pathname],
    ]]]);
  }, [location.pathname, planName, shopDomain]);

  useEffect(() => {
    if (!CRISP_WEBSITE_ID) return;

    runCrisp(['do', enabled ? 'chat:show' : 'chat:hide']);
  }, [enabled]);

  useEffect(() => {
    const handleOpenSupport = () => {
      openCrispChatNow();
    };

    window.addEventListener(CRISP_OPEN_EVENT, handleOpenSupport);
    return () => window.removeEventListener(CRISP_OPEN_EVENT, handleOpenSupport);
  }, []);

  useEffect(() => {
    const supportApi = window.shopify?.support;
    if (!supportApi?.registerHandler) return;

    void supportApi.registerHandler(() => {
      openCrispChatNow();
    }).catch((error) => {
      console.warn('[CrispChat] Failed to register Shopify support handler:', error);
    });

    return () => {
      void supportApi.registerHandler(null).catch(() => {});
    };
  }, []);

  return null;
}
